import { ApiClient } from '@center-technologies/shared-api-clients';
import { Sphere } from 'types/global';

/**
 * Parameters for fetching assignees (either potential or filter).
 */
interface FetchAssigneesParams {
  // Common fields
  schemaSystemName?: string;
  perspectiveSystemName?: string;
  limit?: number;
  page?: number;

  // The type of search endpoint to call:
  // - 'potential-assignees'
  // - 'filter-assignees'
  searchType?: string;

  // Only used when searchType = 'filter-assignees'
  sphereId?: string;
  hops?: number;
  direction?: 'outwards' | 'inwards' | 'both'; // Direction for traversal
  searchTerm?: string;
  filters?: { value: string; property: string }[];
}

/**
 * Response structure from the API.
 */
interface FetchAssigneesResponse {
  spheres: Sphere[];
  pagination: {
    hasNextPage: boolean;
    page: number;
    limit: number;
  };
}

/**
 * Result structure returned by the helper function.
 */
interface FetchAssigneesResult {
  spheres: Sphere[];
  hasNextPage: boolean;
}

export class FetchAssigneesError extends Error {
  details: string | string[];

  constructor(message: string, details: string | string[]) {
    super(message);
    this.details = details;
    this.name = 'FetchAssigneesError';
  }
}

/**
 * Fetches assignee data (potential or filter) with pagination support.
 *
 * @param apiClient - An ApiClient configured for your Graph endpoint
 * @param params - The parameters for the search
 * @returns A promise that resolves to an object containing the spheres and a flag indicating if more pages are available
 * @throws FetchAssigneesError on failure
 */
export async function fetchPotentialAssigneesData(
  apiClient: ApiClient,
  params: FetchAssigneesParams
): Promise<FetchAssigneesResult> {
  const {
    schemaSystemName,
    perspectiveSystemName,
    limit = 50,
    page = 1,
    searchType = 'potential-assignees',
    sphereId,
    hops,
    direction,
    searchTerm,
    filters
  } = params;

  if (!perspectiveSystemName?.trim()) {
    throw new FetchAssigneesError(
      'Invalid parameters',
      'perspectiveSystemName is required'
    );
  }

  // Build request body based on search type
  const requestBody: Record<string, any> = {
    limit,
    page,
    perspectiveSystemName
  };

  if (searchType === 'potential-assignees') {
    // Old logic
    requestBody['schemaSystemName'] = schemaSystemName;
  } else {
    // For 'filter-assignees'
    requestBody['sphereId'] = sphereId;
    if (hops !== undefined) requestBody['hops'] = hops;
    if (direction) requestBody['direction'] = direction;
    if (searchTerm) requestBody['searchTerm'] = searchTerm;
    if (filters) requestBody['filters'] = filters;
  }

  try {
    // ApiClient.post automatically handles the Bearer token header
    const data = await apiClient.post<FetchAssigneesResponse>(
      `/search/${searchType}`,
      requestBody
    );

    return {
      spheres: data.spheres,
      hasNextPage: data.pagination.hasNextPage,
    };
  } catch (err: any) {
    const msg =
      err.response?.data?.error ??
      err.message ??
      'Unknown error fetching assignees data';
    
    console.error('Error fetching assignees data:', msg);
    
    throw new FetchAssigneesError(
      'An unexpected error occurred while fetching data.',
      msg
    );
  }
}